import { Grid, Link, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { copyToClipboard } from "@smartrr/shared/utils/copyToClipboard";
import { typedLocalize, typedLocalizeKey } from "@smartrr/shared/utils/sharedTranslations/localize";
import React from "react";
import { Trans } from "react-i18next";
import { toast } from "react-toastify";
import styled from "styled-components";
import { generatePermalinkWithDiscount } from "@smartrr/shared/utils/generatePermalinkWithDiscount";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { shopifyNumericIdFromGid } from "@smartrr/shared/utils/ensureShopifyGid";
import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import { OrderVariantImage } from "../../../../../Subscription/constants";
import { LoyaltyStoreAccess } from "../../../../store";

import { RedeemVariantOTPProps } from ".";

const StyledGrid = styled(Grid)`
  margin-top: 15px;

  & button {
    /* Stand-alone buttons have 15px - this pulls the margins into the parent, making the DOM cleaner */
    margin-top: 0px;
  }
`;
interface RedeemVariantOTPConfirmedProps extends RedeemVariantOTPProps {
  discountCode: string;
}

export const RedeemVariantOTPConfirmed = ({
  shopifyVariant,
  closeVariantModal,
  discountCode,
  variant,
}: RedeemVariantOTPConfirmedProps) => {
  const isLoading = LoyaltyStoreAccess.useIsLoading();
  const actions = LoyaltyStoreAccess.useActions();
  const theme = useSmartrrTheme();
  const flags = useSmartrrFlags();
  const variantGID = variant ? shopifyNumericIdFromGid(variant.shopifyId) : "";

  return (
    <React.Fragment>
      <Stack alignItems="center" justifyContent="center" py="30px" spacing="10px">
        <Typography variant="h3" style={{ marginBottom: 0 }}>
          {typedLocalize("loyalty.rewardReady")}
        </Typography>
        <OrderVariantImage
          purchasableVariant={shopifyVariant}
          size="medium"
          className={prefixClassName("item-variant-image")}
        />
        <Typography variant="body1">
          <Trans
            i18nKey={typedLocalizeKey("loyalty.codeConfirmedVariant")}
            components={[<span key={0} />, <strong key={1} />, <strong key={2} />]}
            values={{
              productName: variant.variant.productName,
            }}
          />
        </Typography>
        <Typography
          variant="body1"
          sx={{
            width: "100%",
            backgroundColor: theme.modernBadgeBackground,
            textAlign: "center",
            height: theme.modernButtonBorderHeight,
            borderRadius: theme.modernButtonBorderRadius,
          }}
        >
          <span style={{ opacity: "70%", lineHeight: `${theme.modernButtonBorderHeight}px` }}>
            {discountCode}
          </span>
        </Typography>
        <AquariusButton
          filled
          onClick={async () => {
            copyToClipboard(discountCode, typedLocalize("general.copiedToClipboard"), toast);
          }}
          isLoading={isLoading}
        >
          {typedLocalize("loyalty.copyCode")}
        </AquariusButton>
        {variantGID === "" ? (
          <React.Fragment />
        ) : (
          <Typography variant="body1">
            <Trans
              i18nKey={typedLocalizeKey("loyalty.cartVariantAdd")}
              components={[
                <span key={0} />,
                <Link
                  href={generatePermalinkWithDiscount(variantGID, 1, discountCode)}
                  underline="always"
                  sx={{ color: "inherit" }}
                  key={1}
                />,
                <span key={2} />,
              ]}
              values={{
                productName: variant.variant.productName,
              }}
            />
          </Typography>
        )}

        <Typography variant="caption1" sx={{ opacity: "70%", width: "100%", textAlign: "center" }}>
          <span>{typedLocalize("loyalty.codeUnique")}</span>
        </Typography>
      </Stack>

      <StyledGrid
        container
        spacing={"10px"}
        width="100% !important"
        marginLeft={"0px !important"}
        sx={{
          borderTop: `1px solid ${theme.modernLineDividers}`,
          paddingTop: "15px",
        }}
      >
        <AquariusButton
          onClick={() => {
            closeVariantModal();
            actions.otp.redeem.setDiscountCode("");
          }}
          disabled={isLoading}
        >
          {typedLocalize("general.closeBtn")}
        </AquariusButton>
      </StyledGrid>
    </React.Fragment>
  );
};
